/**
 * Learn Mode — what changes on the desktop build.
 *
 * The lessons are authored ONCE, in tour.js, against the phone. This file is the
 * complete list of the places where the desktop app does not agree with them —
 * and nothing else. Keeping it apart from the narrative is deliberate: the
 * lesson reads as a lesson, and every divergence is reviewable in one sitting
 * instead of hiding inside ninety-odd step objects.
 *
 * A step's desktop treatment is one of three things:
 *
 *   null          the step does not exist here. The phone's navigation steps are
 *                 nearly all of these: "tap More, then tap Booking forms" is two
 *                 steps because the phone has five tab slots, and the sidebar
 *                 carries every module at once.
 *
 *   { …patch }    merged over the phone step. Anything a step can carry can be
 *                 patched — anchor, route, say, do, advanceWhen, readOnly.
 *
 *   (absent)      the step is used as written, with one automatic change: the
 *                 phone's verbs are rewritten for a mouse (see VERBS). That
 *                 covers "Tap Create" → "Click Create" without asking anyone to
 *                 hand-maintain sixty near-identical strings.
 *
 * `EXTRA` adds steps that exist only here — the live document preview beside the
 * booking form has no phone equivalent at all, and skipping it would leave the
 * most useful thing on the desktop screen unexplained.
 *
 * THE RULE FOR WRITING A PATCH: patch the copy whenever the phone sentence would
 * be FALSE here, not merely differently phrased. "Tap the card" is true enough
 * once the verb is swapped; "Tap Listings at the bottom" is a lie on a screen
 * whose navigation is down the left, and "we have scrolled the tabs across for
 * you" is a lie on a screen where all eight tabs fit.
 */
import { DEMO_IDS } from './fixtures';

const L = DEMO_IDS;

/* ── the automatic half ─────────────────────────────────────────────────────
   Applied to any say/do this file does not override. Ordered longest-first so a
   phrase is rewritten before the word inside it gets its turn. Case is carried:
   only the first letter can be capitalised in this copy, so a single pair of
   entries per verb is enough. */
const VERBS = [
  [/\bTap\b/g, 'Click'],
  [/\btap\b/g, 'click'],
  [/\bTapping\b/g, 'Clicking'],
  [/\btapping\b/g, 'clicking'],
];

/** Rewrite one line of coach copy for a mouse. Functions are left alone — they
 *  read the live screen (see labelFor) and rewriting their result is this same
 *  pass applied at call time, which tour.js does. */
export const desktopize = (v) => {
  if (typeof v !== 'string') return v;
  return VERBS.reduce((s, [re, to]) => s.replace(re, to), v);
};

/* ── the hand-written half ────────────────────────────────────────────────── */

/**
 * Did the agent choose a value in an antd Select? The selection renders as
 * `.ant-select-selection-item`, whose title is the option label — so this reads
 * what the screen is showing rather than any private component state.
 */
const selectShows = (anchor, text) => () => {
  const el = document.querySelector(`[data-tour="${anchor}"] .ant-select-selection-item`);
  const v = el ? (el.getAttribute('title') || el.textContent || '') : '';
  return v.toLowerCase().includes(text.toLowerCase());
};

/** Has a party been picked onto one of the sale form's two party columns? */
const partyPicked = (anchor) => () =>
  document.querySelectorAll(`[data-tour="${anchor}"] .cid-row`).length > 0;

export const DESKTOP = {
  /* ═══ LESSON 1 · the whole deal ═════════════════════════════════════════ */

  /* ── branch A · from the listing ── */
  'tab-listings': {
    anchor: 'nav-listings',
    say: 'Your properties live in Listings.',
    do: 'Click Listings in the sidebar on the left.',
  },
  'listing-card': {
    say: 'Here is the practice house.',
    do: 'Click the card to open it. Your stock is a wall of photographs here — four across, not one.',
  },
  'form-prefilled': {
    say: 'The property is filled in already, straight from the listing.',
    do: 'It is locked, so you cannot mistype the address onto a legal document.',
  },

  /* ── branch B · straight to the form ──
     The phone needs two steps to reach the list (More, then Booking forms). The
     sidebar is one, so the first is dropped and its framing moves into the second
     — the "no listing this time" line is the point of the whole branch and must
     not be lost with the step that carried it. */
  'd-more': null,
  'd-forms': {
    route: '/',
    anchor: 'nav-forms',
    say: 'No listing this time — the buyer is ready and you write the paper now.',
    do: 'Every form you have written is kept under Booking forms. Click it in the sidebar.',
  },
  'd-fab': {
    say: 'Start a new one.',
    do: 'Click New booking form, at the top right.',
  },
  'd-type': {
    say: 'Nobody picked the form type for you this time.',
    do: 'Sale Type is what kind of deal; Form Type is what kind of paper. Sales + Agreement Letter is an OTP, which is what we are writing — leave them.',
  },

  /* ── the parties ── */
  'buyer-open': {
    say: 'Now the buyer. Nobody has been added yet.',
    do: 'Click the box and start typing to search your contacts.',
  },
  'buyer-entity': {
    say: 'First the form asks: is the buyer a person, or a company?',
    do: 'That choice decides what it wants next — an IC for a person, an SSM number for a company. It is the second box in this panel.',
  },
  'buyer-save': {
    say: 'Save them, and they drop straight onto the form.',
    do: 'Click Create, at the top of the panel. They are kept in your contacts for next time too.',
  },
  'owner-save': {
    say: 'Save the owner.',
    do: 'Click Create at the top of the panel.',
  },

  /* ── the money and the terms ──
     Two phone steps become one: opening a picker and choosing inside it are the
     same gesture with a mouse, and the dropdown here is a real antd popup the
     agent can use in place rather than a sheet the tour has to follow into. */
  'deal-stake-open': {
    anchor: 'form-stakeholder',
    say: 'Who holds the deposit until the sale goes through?',
    do: 'Open the box and choose CID REALTORS SDN BHD. A solicitor can hold it instead — that is what the other two are for.',
    advanceWhenDom: selectShows('form-stakeholder', 'CID REALTORS'),
    // The phone's version of this step only opens the sheet, so it advances on
    // the tap. This one waits for the choice, which is the thing being taught.
    advanceOnTap: false,
  },
  'deal-stake-pick': null,
  'form-save': {
    say: 'Save it, and you have an offer form.',
    do: 'Click Save draft, at the top right. Nothing is in force yet — a draft can be saved half-finished and completed later.',
  },

  /* ── signing ──
     No tabs on this build: the status band, the signatory table and the rendered
     document are all on one page, so the step that goes and finds the Signing tab
     has nothing to find. Every step that lived at ?tab=signing loses the query
     string too, or each one would re-navigate and rebuild the page under the
     agent mid-signature. */
  'signing-tab': null,
  'signing-progress': {
    route: `/forms/${L.otp}`,
    say: 'Now the buyer and the owner sign it — and this table is the whole state of that.',
    do: 'Who has signed, who has been sent a link, and who has not started. Watch it move as each person signs.',
  },
  'sign-open': {
    route: `/forms/${L.otp}`,
    say: 'The buyer is here at the desk with you, so they sign on this computer.',
    do: 'Click On device on the buyer’s row.',
  },
  'sign-draw': {
    route: `/forms/${L.otp}`,
    say: 'Hand them the mouse and let them draw their name.',
    do: 'Draw anything in the box — a trackpad or a touchscreen works too — then click Capture & submit.',
  },
  'sign-second-open': {
    route: `/forms/${L.otp}`,
    say: 'One down. Now the owner signs too.',
    do: 'Click On device on the owner’s row.',
  },
  'sign-second-draw': {
    route: `/forms/${L.otp}`,
    say: 'Same again — draw the owner’s name.',
    do: 'Then click Capture & submit.',
  },
  'all-signed': {
    route: `/forms/${L.otp}`,
    // The phone paints a green "ready to issue" card. Here the same fact is the
    // status band at the top: the state, what it means, and the verbs it allows.
    say: 'Everyone has signed, and the band at the top changed to say so.',
    do: 'It carries every move this state allows. Signed is not the same as in force — one more step.',
  },
  'issue-open': { route: `/forms/${L.otp}` },
  'issue-confirm': { route: `/forms/${L.otp}` },
  'under-offer': {
    say: 'Issuing also booked the house for this buyer, all by itself.',
    do: 'The listing is Under Offer now. Your document is the panel on the right — it has been there all along, and Print / PDF is at the top of it.',
  },
  'under-offer-direct': {
    say: 'The offer is in force.',
    do: 'There was no listing behind this one, so nothing was booked — that is the one thing you give up by starting here. Your document is the panel on the right.',
  },

  /* ── the money ── */
  'inv-tab': {
    say: 'The invoice is waiting for you on the Invoices tab.',
    // The phone has to scroll a rail of seven chips to reach this one. Here they
    // all fit, so saying otherwise would describe a screen the agent is not on.
    do: 'Click Invoices.',
  },
  'inv-open': {
    say: 'Open it to read the actual document.',
    do: 'Click View PDF.',
  },
  /* The viewer's one action is Download here, not Share — an OS share sheet is a
     phone gesture, and at a desk the agent wants the file so they can attach it
     to the mail they are already writing. See components/FileViewer. */
  'inv-share': {
    say: 'Now YOU send it to the buyer — head office never does.',
    do: 'Download saves the PDF to your computer. Attach it to an email or to WhatsApp Web from there, or print it.',
  },
  'inv-close': {
    say: 'Saved. Now you send it on, and chase the payment.',
    do: 'Close the document — head office will not follow this up for you.',
  },
  'rcpt-add': {
    say: 'Record what you received.',
    do: 'Click Record a receipt, at the top right of the card.',
  },
  'rcpt-slip': {
    say: 'Attach the bank-in slip so finance can check it.',
    do: 'Link one already filed on the sale, or upload a new one just below. We have put one on this practice sale already.',
  },
  'rcpt-submit': {
    say: 'Send it to head office.',
    do: 'Click Record receipt, at the foot of the panel.',
  },

  /* ═══ LESSON 2 · the stage board ═══════════════════════════════════════ */
  // Reads identically here — the stage card is in the sale's right-hand rail and
  // Change opens the same list of the same six stages. Verb swap only, so
  // st-receipt needs no patch: unpatched steps auto-rewrite Tap → Click.

  /* ═══ LESSON 3 · finding your way around ═══════════════════════════════
     The phone lesson is a tour of the More hub. There is no More here, so it
     becomes a tour of the sidebar — same four destinations, same distinctions,
     which are the part that actually matters (co-share is not co-agency). */
  more: {
    route: '/',
    anchor: 'nav-home',
    say: 'Everything lives in the sidebar. Nothing is hidden behind a menu.',
    do: 'Three groups: what you work on, the paperwork, then people and tools.',
  },
  'more-forms': { route: '/', anchor: 'nav-forms' },
  'more-letters': { route: '/', anchor: 'nav-letters' },
  'more-coshare': { route: '/', anchor: 'nav-co-share' },
  'earnings-statement': {
    say: 'Your statement shows every ringgit, whenever you want it.',
    do: 'It is made fresh each time — there is nothing to wait for. The button is at the top right of Earnings.',
  },

  /* ═══ LESSON 4 · sharing a deal ════════════════════════════════════════ */
  'cs-more': null,
  'cs-menu': {
    route: '/',
    anchor: 'nav-co-share',
    say: 'Her request is waiting for you.',
    do: 'Co-share is where both sides of these live. Click it in the sidebar.',
  },
  'cs-boxes': {
    anchor: 'cs-box-incoming',
    say: 'Two tabs. Requests to me is what people ask of YOU.',
    do: 'My requests is what you asked of them — you would use that to get onto someone else’s listing. The three tiles above count both.',
  },
  'cs-accept-open': {
    say: 'You want the deal. Say yes.',
    do: 'Click Accept, at the top right. Decline is beside it, and it is final — she would have to ask again.',
  },
  'cs-accept-send': {
    say: 'Send it back to her.',
    do: 'Click Accept co-share. This still creates nothing — it is a handshake, not a deal.',
  },

  /* The sale wizard. Picking a party is one gesture with a mouse, so the phone's
     open-then-choose pairs collapse — and the step now waits for the party to
     actually land on the sale rather than for the option to be clicked. */
  'cs-vendor-open': {
    anchor: 'sale-vendor',
    say: 'Who is selling? Your contacts are already here from earlier deals.',
    do: 'Click the box and choose Lim Wei Sheng.',
    advanceOnTap: false,
    advanceWhenDom: partyPicked('sale-vendor'),
  },
  'cs-vendor-pick': null,
  'cs-purchaser-open': {
    anchor: 'sale-purchaser',
    say: 'And the buyer — the one Farah brought.',
    do: 'Click the box and choose Nurul Aisyah.',
    advanceOnTap: false,
    advanceWhenDom: partyPicked('sale-purchaser'),
  },
  'cs-purchaser-pick': null,
  'cs-split-card': {
    say: 'Both RENs are on the deal, at the split you agreed.',
    do: 'Farah cannot be removed — the sale was raised from her co-share. The percentages are still yours to change in the table, and they must total 100.',
  },
  'cs-atts': {
    say: 'Two RENs on one fee needs an agreement you both sign.',
    do: 'Click the Attachments tab — it lives with the sale’s documents.',
  },
  'cs-form-close': {
    say: 'That is the agreement. Clause 5 is the one worth knowing.',
    do: 'Close it — you sign back on the form.',
  },
  // "on her own phone" is a phone-build turn of phrase for "not on your screen".
  // The point of the step is that this half cannot be done for her, and the
  // device she happens to use is beside it.
  'cs-peer-signs': {
    say: 'Farah signs hers, on her own device.',
    do: 'You cannot do this bit for her — which is the point of a document you both sign.',
  },

  /* ═══ LESSON 5 · the owner’s letter ════════════════════════════════════ */
  'ch-more': null,
  'ch-menu': {
    route: '/',
    anchor: 'nav-forms',
    say: 'You already wrote the letter and the owner signed it last week.',
    do: 'Letters and offers live in the same place. Click Booking forms in the sidebar.',
  },
  'ch-open': {
    say: 'ALS — Authority to Sell. That is the owner appointing you.',
    do: 'ALR is the rental version. OTP and OTR are what a BUYER signs — different paper, different signer.',
  },
  'ch-publish': {
    say: 'Put it up.',
    do: 'Publish makes it visible, and it is at the top of the page. Save draft keeps it to yourself — a draft is private and never public.',
  },
  'ch-price-locked': {
    say: 'The price and the deposit ladder came across too.',
    do: 'These are pre-filled, NOT locked — change them if the deal was actually struck at a different number, and watch the letter on the right change with them.',
  },
};

/**
 * Steps that exist only on this build, keyed by the step they follow.
 *
 * There is exactly one, and it earns its place: the whole left/right shape of the
 * desktop booking form is the live document, and an agent who is not told what
 * the right-hand panel is will read it as a picture of a form rather than as the
 * thing they are about to ask two people to sign.
 */
const PREVIEW_STEP = {
  id: 'd-preview',
  chapter: 'THE PAPER',
  anchor: 'form-preview',
  say: 'That panel is not a picture of the form — it is the document itself.',
  do: 'It re-renders as you type, from the same template the signed copy prints from. If a figure reads wrong there, it is wrong.',
};

export const EXTRA = {
  // Once per run, not twice: the fork's two branches arrive at the form by
  // different routes and rejoin at the buyer, so the step hangs off the LAST step
  // of each branch and inherits that branch's `path`. Hanging it off the shared
  // step after them would have put it on top of an open contact dropdown.
  'form-prefilled': [PREVIEW_STEP],
  'd-address': [PREVIEW_STEP],
};

/**
 * Fold this file into one lesson's step list.
 *
 * Returns the steps as the desktop build actually walks them: dropped steps gone,
 * patches merged, desktop-only steps inserted, and the verbs of everything that
 * was left alone rewritten for a mouse.
 */
export function toDesktop(steps) {
  const out = [];
  for (const step of steps) {
    const patch = Object.prototype.hasOwnProperty.call(DESKTOP, step.id) ? DESKTOP[step.id] : undefined;
    if (patch === null) continue;
    const merged = patch ? { ...step, ...patch } : step;
    // Only untouched copy is auto-rewritten. A patched line was written for this
    // screen by hand and must not be second-guessed by a regex.
    out.push({
      ...merged,
      say: patch?.say !== undefined ? merged.say : desktopize(merged.say),
      do: patch?.do !== undefined ? merged.do : desktopize(merged.do),
      gateHint: desktopize(merged.gateHint),
    });
    /* An inserted step inherits the SCREEN of the step it follows — its route,
       the router state that screen needs, the block it belongs to and the branch
       it is on. Without that, Back or a resume landing on it would navigate to a
       bare /forms/new: a blank, unlinked form, with nothing for it to point at. */
    for (const extra of EXTRA[step.id] || []) {
      out.push({
        route: merged.route,
        navState: merged.navState,
        block: merged.block,
        path: merged.path,
        ...extra,
      });
    }
  }
  return out;
}

/* A patch for a step id that no longer exists is silent — it simply never fires,
   and the lesson quietly keeps the phone's copy on a screen that contradicts it.
   Shout about it instead, once, on the build that can act on it. */
if (import.meta.env.DEV) {
  // Imported lazily to avoid a cycle: tour.js imports this file.
  import('./tour').then(({ LESSONS }) => {
    const ids = new Set(LESSONS.flatMap((l) => l.steps).map((s) => s.id));
    const stray = [...Object.keys(DESKTOP), ...Object.keys(EXTRA)].filter((k) => !ids.has(k));
    if (stray.length) {
      // eslint-disable-next-line no-console
      console.warn(`[learn] tourDesktop.js patches steps that do not exist: ${stray.join(', ')}. Rename or remove them — a patch keyed on a dead id is a phone screen being described on a desktop one.`);
    }
  });
}
